import { LOCALE } from '@constants/locale';
import {
    BILLING_TIME_MAP,
    BILLING_TYPE_MAP,
    CURRENCY_MAP,
    FREEMIUM_RETIREMENT_DATE,
    SUBSCRIBE_STATE,
    SUBSCRIPTION_STATUS,
    TRIAL_DURATION_DAYS,
} from '@constants/billing';

export const formatCurrency = (currency, amount, decimals = 0) =>
    `${CURRENCY_MAP[currency] ?? ''}${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(amount ?? 0)}`;

const NON_LEGACY_PLANS = [BILLING_TYPE_MAP.PLUSPOINT, BILLING_TYPE_MAP.FREEMIUM, BILLING_TYPE_MAP.TRIAL];

export const getActiveSubscriptions = account =>
    (account?.subscriptions ?? []).filter(item => item.status === SUBSCRIPTION_STATUS.ACTIVE);

const GRACE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
export const getEntitledSubscriptions = account => {
    const now = Date.now();
    return (account?.subscriptions ?? []).filter(item => {
        const expiresAt = new Date(item.expiresAt).getTime();
        if (item.status === SUBSCRIPTION_STATUS.ACTIVE || item.status === SUBSCRIPTION_STATUS.FAILED) {
            return expiresAt > now - GRACE_WINDOW_MS;
        }
        return item.status === SUBSCRIPTION_STATUS.CANCELED && expiresAt > now;
    });
};

export const getCurrentSubscription = account => {
    const active = getActiveSubscriptions(account);
    const candidates = [...active, ...getEntitledSubscriptions(account).filter(item => !active.includes(item))];
    const freePlans = [BILLING_TYPE_MAP.FREEMIUM, BILLING_TYPE_MAP.TRIAL];

    return candidates.find(item => !freePlans.includes(item.billingPlanName)) ?? candidates[0];
};

export const getPlanName = account => getCurrentSubscription(account)?.billingPlanName;

export const isLegacyPaidPlan = account => {
    const planName = getPlanName(account);
    return Boolean(planName) && !NON_LEGACY_PLANS.includes(planName);
};

export const isAccountLocked = account => account?.isAccountLocked ?? false;

export const needsSubscribe = account => {
    const planName = getPlanName(account);
    return !planName || planName === BILLING_TYPE_MAP.TRIAL || planName === BILLING_TYPE_MAP.FREEMIUM;
};

export const getSubscribeState = account => {
    const cohortCutoff = new Date(FREEMIUM_RETIREMENT_DATE);
    cohortCutoff.setDate(cohortCutoff.getDate() - TRIAL_DURATION_DAYS);

    return new Date(account?.createdAt) < cohortCutoff
        ? SUBSCRIBE_STATE.FREEMIUM_RETIRED
        : SUBSCRIBE_STATE.TRIAL_EXPIRED;
};

export const getPricingPlans = (locale, locationsNumber) => {
    const isSingleLocation = locationsNumber === 1;
    switch (locale) {
        case LOCALE.BRAZIL:
            return {
                [BILLING_TIME_MAP.MONTHLY]: {
                    users: 99,
                    directories: 199,
                    price: {
                        [BILLING_TYPE_MAP.STARTER]: isSingleLocation ? 249 : 199,
                        [BILLING_TYPE_MAP.PROFESSIONAL]: isSingleLocation ? 499 : 349,
                        [BILLING_TYPE_MAP.ENTERPRISE]: 0,
                        [BILLING_TYPE_MAP.PLUSPOINT]: 149,
                    },
                    symbol: 'R$',
                    currency: 'BRL',
                },
                [BILLING_TIME_MAP.ANNUAL]: {
                    users: 990,
                    directories: 1990,
                    price: {
                        [BILLING_TYPE_MAP.STARTER]: isSingleLocation ? 2490 : 1990,
                        [BILLING_TYPE_MAP.PROFESSIONAL]: isSingleLocation ? 4990 : 3490,
                        [BILLING_TYPE_MAP.ENTERPRISE]: 0,
                        [BILLING_TYPE_MAP.PLUSPOINT]: 1490,
                    },
                    symbol: 'R$',
                    currency: 'BRL',
                },
            };
        case LOCALE.PORTUGAL:
        case LOCALE.SPAIN:
        case LOCALE.POLAND:
        case LOCALE.GERMANY:
            return {
                [BILLING_TIME_MAP.MONTHLY]: {
                    users: 18,
                    directories: 39,
                    price: {
                        [BILLING_TYPE_MAP.STARTER]: isSingleLocation ? 45 : 36,
                        [BILLING_TYPE_MAP.PROFESSIONAL]: isSingleLocation ? 95 : 65,
                        [BILLING_TYPE_MAP.ENTERPRISE]: 0,
                        [BILLING_TYPE_MAP.PLUSPOINT]: 29,
                    },
                    symbol: '€',
                    currency: 'EUR',
                },
                [BILLING_TIME_MAP.ANNUAL]: {
                    users: 180,
                    directories: 390,
                    price: {
                        [BILLING_TYPE_MAP.STARTER]: isSingleLocation ? 450 : 360,
                        [BILLING_TYPE_MAP.PROFESSIONAL]: isSingleLocation ? 950 : 650,
                        [BILLING_TYPE_MAP.ENTERPRISE]: 0,
                        [BILLING_TYPE_MAP.PLUSPOINT]: 290,
                    },
                    symbol: '€',
                    currency: 'EUR',
                },
            };
        case LOCALE.UNITED_KINGDOM:
            return {
                [BILLING_TIME_MAP.MONTHLY]: {
                    users: 15,
                    directories: 35,
                    price: {
                        [BILLING_TYPE_MAP.STARTER]: isSingleLocation ? 39 : 32,
                        [BILLING_TYPE_MAP.PROFESSIONAL]: isSingleLocation ? 79 : 55,
                        [BILLING_TYPE_MAP.ENTERPRISE]: 0,
                        [BILLING_TYPE_MAP.PLUSPOINT]: 25,
                    },
                    symbol: '£',
                    currency: 'GBP',
                },
                [BILLING_TIME_MAP.ANNUAL]: {
                    users: 150,
                    directories: 350,
                    price: {
                        [BILLING_TYPE_MAP.STARTER]: isSingleLocation ? 390 : 320,
                        [BILLING_TYPE_MAP.PROFESSIONAL]: isSingleLocation ? 790 : 550,
                        [BILLING_TYPE_MAP.ENTERPRISE]: 0,
                        [BILLING_TYPE_MAP.PLUSPOINT]: 250,
                    },
                    symbol: '£',
                    currency: 'GBP',
                },
            };
        default:
            return {
                [BILLING_TIME_MAP.MONTHLY]: {
                    users: 19,
                    directories: 39,
                    price: {
                        [BILLING_TYPE_MAP.STARTER]: isSingleLocation ? 49 : 39,
                        [BILLING_TYPE_MAP.PROFESSIONAL]: isSingleLocation ? 99 : 69,
                        [BILLING_TYPE_MAP.ENTERPRISE]: 0,
                        [BILLING_TYPE_MAP.PLUSPOINT]: 29,
                    },
                    symbol: '$',
                    currency: 'USD',
                },
                [BILLING_TIME_MAP.ANNUAL]: {
                    users: 190,
                    directories: 390,
                    price: {
                        [BILLING_TYPE_MAP.STARTER]: isSingleLocation ? 490 : 390,
                        [BILLING_TYPE_MAP.PROFESSIONAL]: isSingleLocation ? 990 : 690,
                        [BILLING_TYPE_MAP.ENTERPRISE]: 0,
                        [BILLING_TYPE_MAP.PLUSPOINT]: 290,
                    },
                    symbol: '$',
                    currency: 'USD',
                },
            };
    }
};

const CURRENCY_TO_PRICING_LOCALE = {
    BRL: LOCALE.BRAZIL,
    EUR: LOCALE.PORTUGAL,
    GBP: LOCALE.UNITED_KINGDOM,
    USD: null,
};

export const getPricingPlansByCurrency = (currency, fallbackLocale) => {
    const key = currency?.toUpperCase();
    return key in CURRENCY_TO_PRICING_LOCALE
        ? getPricingPlans(CURRENCY_TO_PRICING_LOCALE[key])
        : getPricingPlans(fallbackLocale);
};
